import { type RENDER_MODES } from '../../../shared/constants';
import {
  type ActiveFiltersState,
  type ActiveGraphPointState,
  type ActiveWidgetItem,
  type AdvancedFilterState,
  type InsightItemTypes,
  type ListRowAccountState,
  type ListRowState,
  type TabFilterState,
  type TrackedFunctionData,
  type ViolationsActionManagerState,
  type WidgetDataListItem,
  type WidgetMenuItem,
} from '../../../shared/types';
import { type FilterCacheState } from '../hooks';
import { type BaseFilterState } from '../utils';

export enum ViolationsTabType {
  SLICE_NAME = 'aag/violationsTab',
  API_SLICE_NAME = 'aag/violationsTabApi',
}

export interface ColumnState {
  label: string;
  value: string;
  isChecked: boolean;
  isDisabled: boolean;
}

export type SortState = Record<'value' | 'order', string> | null;

export type RenderModeState = (typeof RENDER_MODES)[keyof typeof RENDER_MODES];

interface ViolationsTabControlsState {
  isInsightsOpen: boolean;
  renderMode: RenderModeState;
  tabFilter: TabFilterState[];
  filters: AdvancedFilterState[];
  activeFilters: ActiveFiltersState[];
  isFilterBarOpen: boolean;
  tabInAction: string | null;
  sortInAction: SortState;
  activeColumns: ColumnState[];
  filterCache: FilterCacheState;
  activeInsight: InsightItemTypes | null;
  searchKey: string | null;
  activeGraphPoint: ActiveGraphPointState | null;
  activeRowContext: ViolationsTabSidePanelRowContextState | null;
  widgets: WidgetMenuItem[];
  activeWidgetItem: ActiveWidgetItem | null;
}

export interface ViolationsTabListState {
  isListLoading: boolean;
  totalCount: number | null;
  rowIdToTransition: number | string | null;
  isRefresh: boolean;
}

export interface ActionInfoState {
  id: string;
  riskId: string;
  ruleset: string;
  riskName: string;
  allowedActions: string[];
}

interface ViolationsTabBulkActionState {
  isFooterVisible: boolean;
  bulkCount: number;
  isBulkChecked: boolean;
  isClear: boolean;
  sodKeys: {
    in: string[];
    notIn: string[];
  };
  actionInfo: ActionInfoState[];
}

export interface ViolationsTabSidePanelRowContextState {
  id: string;
  title?: string;
  displayName?: string;
  username?: string;
  allowedActions?: string[];
  isReadOnlyActions?: boolean;
  isExpiredCount?: number;
  isExpiringSoonCount?: number;
  mitigatingControlCount?: number;
  functions?: TrackedFunctionData[];
  accounts?: ListRowAccountState[];
  startDate?: string | null;
  endDate?: string | null;
  riskId?: string;
  rulesetName?: string;
}

export interface ViolationsTabSidePanelGroupRowContextState {
  current: ViolationsTabSidePanelRowContextState;
  prev?: ViolationsTabSidePanelRowContextState;
  next?: ViolationsTabSidePanelRowContextState;
}

interface ViolationsTabSidePanelState {
  isSidePanelVisible: boolean;
  rowContext: ViolationsTabSidePanelGroupRowContextState;
}

export interface ViolationsTabState {
  isLoading: boolean;
  controls: ViolationsTabControlsState;
  list: ViolationsTabListState;
  bulkaction: ViolationsTabBulkActionState;
  sidepanel: ViolationsTabSidePanelState;
  actionManager: ViolationsActionManagerState | null;
}

export interface GetFilterOptionsParams {
  searchText: string;
  range: number;
}

// Query inputs
export interface FiltersGeneralProps {
  max?: string;
  offset?: string;
  search?: string;
  fields?: string;
  sort?: string;
  order?: string;
  key?: string;
  isValue?: boolean;
}

export interface SodViolationsListProps {
  filters: {
    sodStatuses: string[];
    risk: {
      riskIds?: number[];
      riskName?: string[];
      actualRisk?: string[];
      riskPriority?: string[];
      riskType?: string[];
      businessProcess?: string[];
    };
    account?: {
      accountKeys?: number[];
      accountType?: string[];
      userLock?: string[];
    };
    ruleset?: {
      rulesetKeys?: number[];
    };
    application?: {
      endpointKeys?: number[];
      applicationName?: string[];
    };
    user?: {
      userKeys?: number[];
      userName?: string[];
      firstName?: string[];
      lastName?: string[];
      costCenter?: string[];
      employeeId?: string[];
      jobCode?: string[];
    };
    function?: {
      functionKeys?: number[];
      status?: string[];
      functionName?: string[];
    };
    entitlement?: {
      entitlementValueKeys?: number[];
      entitlementType?: string[];
      value?: string[];
    };
    mitigatingControl?: {
      mitigatingControlKeys?: number[];
      mitigatingControlName?: string[];
      mcExpiryDate?: {
        startDate?: string | null;
        endDate?: string | null;
      };
      mcAssignedStartDate?: {
        startDate?: string | null;
        endDate?: string | null;
      };
      mcAssignedEndDate?: {
        startDate?: string | null;
        endDate?: string | null;
      };
    };
    globalSearch?: string;
    insightRequests?: Pick<InsightItemTypes, 'id' | 'filters'>[];
  };
  pagination: {
    offset: number;
    max: number;
  };
  sort: {
    field: string;
    order: string;
  };
}

// Actions
export interface SodReOpenRiskProps {
  filters?: Pick<SodViolationsListProps, 'filters'>;
  pagination: Pick<SodViolationsListProps, 'pagination'>;
  sodIds: {
    in: number[];
    notIn: number[];
  };
  comment: string;
}

export interface SodConsultRiskProps {
  filters: Pick<SodViolationsListProps, 'filters'>;
  pagination: Pick<SodViolationsListProps, 'pagination'>;
  sodIds: {
    in: number[];
    notIn: number[];
  };
  data: {
    startDate: string;
    endDate: string;
    comment: string;
  };
}

export interface SodExtendReviewEndDateRiskProps {
  filters: Pick<SodViolationsListProps, 'filters'>;
  pagination: { offset: number; max: number };
  sodIds: {
    in: number[];
    notIn: number[];
  };
  data: {
    endDate: string;
    comment: string;
  };
}

export interface SodRemediateRiskProps {
  violationId: string;
  payload: {
    functionId: number;
    comments: string;
  };
}

export interface SodRemoveMitigatingControlProps {
  violationId: number;
  mcKeys: number[];
  comments: string;
}

export interface SodMitigatingControlsEndDateProps {
  filters?: Pick<SodViolationsListProps, 'filters'>;
  pagination: Pick<SodViolationsListProps, 'pagination'>;
  sodIds: {
    in: number[];
    notIn: number[];
  };
  endDate: string;
  comment: string;
}

export interface SodGenerateSyncReportProps {
  reportType: string;
  format: string;
  locale: string;
  userId: string;
  columnConfig: {
    columnOrder: Record<string, number>;
    selectedColumns: string[];
    columnDisplay?: Record<string, string>;
  };
  filters: {
    violationIds: string[];
  };
}

export interface SodMitigateProps {
  sodIds: {
    in: number[];
    notIn: number[];
  };
  allMitigatingControls: {
    id: number;
    startDate: string;
    endDate: string;
    comments: string;
  }[];
  filters: BaseFilterState;
}

export interface GetUserDetailsProps {
  usernameQuery: string;
}

export interface FunctionAnalysisState {
  sameEntitlementsInAllFunctions: boolean;
  functionEntitlementCount: {
    entitlementCount: number;
    functionId: number;
  }[];
}

export interface GetMitiatingControlListProps extends FiltersGeneralProps {
  ruleset: string;
  mitigatingControl?: string;
  businessProcess?: string;
  status?: string;
  priority?: string;
  columnToSort?: string;
}

export interface GetRecommendedMitiatingControlListProps {
  riskId: number;
  max?: number;
  offset?: number;
  order: string;
  columnToSort?: string;
  searchText: string;
  recommended?: boolean;
}

// API responses
export interface GetApiResponse {
  status?: number;
  errorCode?: string;
  displayRecords?: number;
  totalRecords?: number;
  displayCount?: number;
  displaycount?: number;
  message?: string;
}

export interface GetTabCountApiResponse {
  openCount: number;
  mitigatedCount: number;
  closedCount: number;
  inReviewCount: number;
  remediatedCount: number;
}

export interface ColumnPickerState {
  id: string;
  localizationKey: string;
  isDefault: boolean;
}

export interface GetColumnPickerApiResponse {
  status: number;
  data: Record<string, ColumnPickerState[]>;
}

export interface GetRiskApiResponse extends GetApiResponse {
  data?: {
    riskId: number;
    riskName: string;
  }[];
}

export interface GetBusinessProcessApiResponse extends GetApiResponse {
  data?: {
    businessProcessKey: number;
    businessProcess: string;
  }[];
}

export interface GetFunctionsApiResponse extends GetApiResponse {
  data?: {
    id: number;
    functionName: string;
  }[];
}

export interface GetRulesetsApiResponse extends GetApiResponse {
  data?: {
    id: number;
    rulesetName: string;
  }[];
}

export interface GetApplicationEndpointApiResponse extends GetApiResponse {
  endpoints?: {
    id: string;
    displayName: string;
  }[];
}

export interface GetUserFiltersApiResponse extends GetApiResponse {
  userlist?: {
    firstname: string;
    lastname: string;
    username: string;
    userKey: string;
    costcenter: string;
  }[];
}

export interface GetAccountsApiResponse extends GetApiResponse {
  Accountdetails?: {
    name: string;
    accountKey: string;
  }[];
}

export interface GetEntitlementsApiResponse extends GetApiResponse {
  entitlementsCount?: number;
  Entitlementdetails?: {
    entitlement_valuekey: string;
    entitlement_value: string;
  }[];
}

export interface GetEntitlementTypessApiResponse extends GetApiResponse {
  entitlementTypeDetails?: {
    displayName: string;
    entitlementTypeKey: number;
  }[];
}

export interface GetMitigatingControlsApiResponse extends GetApiResponse {
  data?: {
    mitigatingControlId: number;
    mitigatingControl: string;
  }[];
}

export interface GetInsightDetailsApiResponse extends GetApiResponse {
  data?: {
    id: number;
    title: string;
    filters: object;
    description: string;
  };
}

export interface GetViolationsGraphResponse extends GetApiResponse {
  data?: {
    clusters: ActiveGraphPointState[];
    maxViolationAge: number;
    maxViolationScore: number;
    totalViolations: number;
    timeRange: string;
    updatedTime: string;
  };
}

export interface GetViolationsGraphClusterResponse extends GetApiResponse {
  data?: ListRowState[];
  page: { max: number; offset: number };
  sort: { order: string; columnToSort: string };
}

export interface GetWidgetsResponse extends GetApiResponse {
  data?: { widgetName: string; top5Objects: WidgetDataListItem[] };
}

export interface GetViolationActionResponse extends GetApiResponse {
  data: {
    count: number;
  };
}

export interface GetFunctionAnalysisResponse extends GetAccountsApiResponse {
  data: FunctionAnalysisState;
}

export interface GetRemediateOptionsResponse {
  key: string;
  category: string;
  configData: string;
  possibleValues: string;
}

export interface GetMitigatingControlListResponse {
  data: {
    mitigatingControlId: number;
    mitigatingControl: string;
    ruleset: string;
    expiryDate: string;
    priority: string;
    status: string;
    comments: string | null;
    description: string;
    owner: string | null;
    businessProcess: string | number | null;
  }[];
}
